/**
 * 「電話番号が変わる方」の一覧（#444。前のスマホのログインが切れる・#408）。
 *
 * 押す前の確認の画面で、**どなたの番号がどう変わるか**をお見せします。番号そのものは出さず、
 * 前と後の**下4桁**だけにします。前の番号が空だった方（番号が入るだけの方）は、切れるログインが
 * 無いのでここには出ません（サーバが同じ決まりで選んで返します）。
 *
 * **取り込む方ご自身の番号が変わるときは、赤い帯**でお知らせします。取り込むとこの画面からも
 * ログアウトし、新しい番号で入り直すことになるため、書き間違いがあると入れなくなります。
 */
import type { ReactNode } from 'react';
import type { RosterPhoneChange } from '../api/types.js';
import { DataTable } from './ui/DataTable.js';
import { ResultBanner } from './ui/ResultBanner.js';

/** 一度に開いて見せる人数（これを超えたら折りたたむ）。 */
const VISIBLE_WITHOUT_OPENING = 20;

export function RosterPhoneChanges({ rows }: { rows: RosterPhoneChange[] }): ReactNode {
  if (rows.length === 0) return null;
  const includesYou = rows.some((row) => row.isYou);
  const table = (
    <DataTable label="電話番号が変わる方">
      <thead>
        <tr>
          <th scope="col">行</th>
          <th scope="col">お名前</th>
          <th scope="col">いまの番号</th>
          <th scope="col">新しい番号</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.lineNumber}>
            <td>{`${row.lineNumber}行目`}</td>
            <td>
              {`${row.displayName}さん`}
              {row.isYou ? <strong>（あなた）</strong> : null}
            </td>
            <td>{`下4桁 ${row.previousLast4}`}</td>
            <td>
              {`下4桁 ${row.nextLast4}`}
              {/* 下4桁が同じだと「0012 → 0012」で変わらないように見えるので、言葉で添える（#475 W-15） */}
              {row.nextLast4 === row.previousLast4
                ? '（下4桁は同じで、ほかの桁が変わります）'
                : null}
            </td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
  return (
    <section>
      <h3>電話番号が変わる方（{rows.length}名）</h3>
      {includesYou ? (
        <ResultBanner
          tone="error"
          message="あなたの電話番号が変わります。取り込むとこの画面からもログアウトし、新しい番号で入り直していただきます。"
          nextStep="番号に誤りがないかご確認ください。誤りがあると、新しい番号に認証番号が届かず入れなくなります。"
        />
      ) : null}
      <p>
        取り込むと、この方々の前のスマホのログインは切れます。新しい電話番号でログインし直していただくよう、ご本人にお伝えください。
      </p>
      {rows.length > VISIBLE_WITHOUT_OPENING ? (
        <details open={includesYou}>
          <summary>{rows.length}名すべてを見る</summary>
          {table}
        </details>
      ) : (
        table
      )}
    </section>
  );
}
